import { useCallback, useContext, useEffect } from "react"
import { useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import _get from "lodash/get"

import { AnimatedWindowCallbackContext } from "context/window/AnimatedWindowProvider"

import useUpsertBankAccount from "hooks/queries/bankAccount/useUpsertBankAccount"
import useGetBankAccount from "hooks/queries/bankAccount/useGetBankAccount"

import BankAccountPolicy from "components/modules/policy/BankAccountPolicy"

const schema = yup.object({
  accountHolderName: yup
    .string()
    .required("*이름(예금주)를 입력해주세요")
    .transform((name) => {
      const replaceName = name.replace(/\s/gi, "")
      return replaceName
    }),
  accountNumber: yup
    .string()
    .required("*계좌번호를 입력해 주세요")
    .matches(/^[0-9]+$/, "*‘-’없이 숫자만 입력해 주세요"),
  isPrivacyConsented: yup.boolean().oneOf([true], "* 약관내용에 동의해 주세요"),
})

const useBankAccountForm = (id, onClose, onComplete) => {
  const userInfo = useSelector((state) => state.userInfo)
  const { openWindow, id: windowId } = useContext(AnimatedWindowCallbackContext)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      accountHolderName: "",
      accountNumber: "",
      isPrivacyConsented: false,
    },
    resolver: yupResolver(schema),
    mode: "onTouched",
  })

  const { data: bankAccountInfo } = useGetBankAccount(userInfo.id, {
    enabled: userInfo.id !== null,
    cacheTime: 0,
  })
  const { mutate: upsertBankAccountMutate } = useUpsertBankAccount()

  const getError = useCallback(
    (fieldName) => {
      const fieldErr = _get(errors, fieldName)
      const errorMsg = typeof fieldErr?.message === "string" ? fieldErr?.message : undefined

      return errorMsg
    },
    [errors]
  )

  const handleClickPolicyConsented = () => {
    openWindow({
      id: windowId,
      Component: <BankAccountPolicy />,
      headerProps: {
        title: "개인정보 제3자 제공",
      },
    })
  }

  const onSubmit = useCallback(
    (data) => {
      upsertBankAccountMutate(data, {
        onSuccess: () => {
          reset({
            accountHolderName: "",
            accountNumber: "",
            isPrivacyConsented: false,
          })
          onClose(id)

          if (onComplete) {
            onComplete()
          }
        },
        onError: (err) => {
          const errData = err.response.data.error.res_data
          if (errData.detail_code === "BANK_ACCOUNT_NUMBER_ALREADY_EXIST") {
            setError("accountNumber", { message: "이미 등록된 계좌번호입니다." })
          }
        },
      })
    },
    [upsertBankAccountMutate, reset, onClose, id, onComplete, setError]
  )

  useEffect(() => {
    if (bankAccountInfo) {
      if (bankAccountInfo.data.bank_accounts.length > 0) {
        const { account_holder_name: accountHolderName, account_number: accountNumber } =
          bankAccountInfo.data.bank_accounts[0]
        setValue("accountHolderName", accountHolderName)
        setValue("accountNumber", accountNumber)
      }
    }
  }, [bankAccountInfo, setValue])

  return { register, watch, onSubmit: handleSubmit(onSubmit), getError, handleClickPolicyConsented }
}

export default useBankAccountForm
